import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { TemplateService } from "../services/settings/template.service";
import { TemplateConfigItem } from "../models/template-config-item";
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { inject } from "@angular/core";
import { TemplateConfigFieldItem } from "../models/template-config-field-item";
import _ from "lodash";
import { ExportTemplateFieldFormatOption } from "../models/export-template-field-format-option";
import { of } from "rxjs";

type TemplateConfigState = {
    templateId: number;
    currentTemplate: TemplateConfigItem;
    originalTemplate: TemplateConfigItem;
    fieldItems: TemplateConfigFieldItem[];
    isNew: boolean;
    loading: boolean;
    formatOptions: ExportTemplateFieldFormatOption[];
};

const initialState: TemplateConfigState = {
    currentTemplate: new TemplateConfigItem(),
    originalTemplate: new TemplateConfigItem(),
    fieldItems: [],
    templateId: 0,
    isNew: true,
    loading: false,
    formatOptions: []
};

/*** HELPERS ***/

const getExportTemplateFieldFormatOptions = (store) => {
    const service = inject(TemplateService);
    service.getExportTemplateFieldFormatOptions().subscribe((result) => {
        patchState(store, {formatOptions: result});
    });
}

const save = (service: TemplateService, template: TemplateConfigItem) =>
  service.addUpdateTemplate(template);

const updateFlow = (
  store: any,
  service: TemplateService,
  template: TemplateConfigItem
): import('rxjs').Observable<string | boolean> => {
  const updatedTemplate = _.cloneDeep(template);

  return save(service, updatedTemplate).pipe(
    switchMap((result: any) => {
      const id = result?.ID;
      const validName = !(result?.InvalidName);

      if (!validName || !id) {
        return of('invalid-name');
      }

      return getTemplateConfigFieldItems(id, service).pipe(
        tap((items) => {
          updatedTemplate.ID = id;
          updatedTemplate.FieldItems = items;
          patchState(store, {
            templateId: id,
            currentTemplate: updatedTemplate,
            fieldItems: items
          });
        }),
        map(() => true)
      );
    }),
    catchError((err) => {
      console.error('updateFlow failed', err);
      return of(false);
    }),
    // cache the last emission if multiple subscribers attach
    shareReplay({ bufferSize: 1, refCount: true })
  );
};

const remove = (store, service: TemplateService) => {
    const state = getState(store) as TemplateConfigState;
    return service?.deleteTemplate(state.currentTemplate.ID).pipe(
        map((result) => result)
    );
}

const getTemplateConfigFieldItems = (id, service: TemplateService) => {
    return service.getTemplateConfigFieldItems(id)
        .pipe(
            map((items) => items)
        );
}

const updateSortIdOfFieldItems = (fieldItems: TemplateConfigFieldItem[]) => {
    const updatedFieldItems: TemplateConfigFieldItem[] = [];

    fieldItems.forEach((item: TemplateConfigFieldItem) => {
        const newItem = _.cloneDeep(item);
        newItem.SortID = fieldItems.indexOf(item) + 1;
        updatedFieldItems.push(newItem);
    });

    return updatedFieldItems;
}

const revertToInitialState = (store) => {
    patchState(store, initialState);
}

const getDefaultFormat = (store, dataType?: string, fieldName?: string) => {
    if (!dataType) { return; }
    // Default to MM/DD/YYYY for date fields
    if (dataType === 'date') {
        return store.formatOptions().find(o => o.Name === 'MM/DD/YYYY')?.ID;
    } else if (dataType === 'alphanumeric') {
        const format = fieldName === 'MBI' ? 'Upper Case' : 'Proper Case';
        return store.formatOptions().find(o => o.FieldDataType === 'alphanumeric' && o.Name === format)?.ID;
    } else if (dataType === 'alpha') {
        return store.formatOptions().find(o => o.FieldDataType === 'alpha' && o.Name === 'Proper Case')?.ID;
    } else {
        return;
    }
};

/*** STORE ***/

export const TemplateConfigStore = signalStore(
    withState(initialState),
    /** METHODS AVAILABLE TO COMPONENTS **/
    withMethods((store, service = inject(TemplateService)) => ({
        setTemplateId(id?: number): void {
            patchState(store, {templateId: id});
        },
        setCurrentNameAndType(name: string, typeID: number) {
            const state = getState(store);
            const currentTemplate = _.cloneDeep(state.currentTemplate);
            currentTemplate.Name = name;
            currentTemplate.TypeID = typeID;
            return new Promise<TemplateConfigItem>((resolve, reject) => {
                patchState(store, {currentTemplate: currentTemplate});
                resolve(currentTemplate);
                reject(new Error(`Error setting current template name and type.`));
            });
        },
        setCurrentTemplate(input: TemplateConfigItem) {
            return new Promise<TemplateConfigItem>((resolve, reject) => {
                patchState(store, {currentTemplate: input, fieldItems: input.FieldItems});
                resolve(input);
                reject(new Error(`Error setting current template.`));
            })
        },
        addRemoveFieldItems(index: number, input?: TemplateConfigFieldItem, isReorder?: boolean) {
            const state = getState(store);
            const apiItem = _.cloneDeep(state.currentTemplate);
            if (input && !input.FormatID) {
                input.FormatID = getDefaultFormat(store, input?.DataType, input?.Field);
            }
            // if we're moving the current field item in the existing list, remove it at the old index and add it at the new one
            if (input && isReorder) {
                apiItem.FieldItems = apiItem.FieldItems.filter(item => item.ID != input.ID || apiItem.FieldItems.indexOf(item) == index);
                apiItem.FieldItems.splice(index, 0, input);
            // if we're adding field items to an existing list of field items attached to the template, splice at index
            } else if (input && apiItem.FieldItems && apiItem.FieldItems?.length > 0) {
                apiItem.FieldItems.splice(index, 0, input);
            // if we're adding the first field item attached to the template, instantiate the field items list with the new field item
            } else if (input) {
                apiItem.FieldItems = [input];
            // if we're removing a field item, delete 1 item at the given index
            } else {
                apiItem.FieldItems.splice(index, 1);
            }
            if (apiItem.FieldItems.length > 0) {
                apiItem.FieldItems = updateSortIdOfFieldItems(apiItem.FieldItems);
            }
            return updateFlow(store, service, apiItem);
        },
        updateFieldItem(input: TemplateConfigFieldItem) {
            const state = getState(store);
            const currentTemplate = _.cloneDeep(state.currentTemplate);
            const updatedFieldItems: TemplateConfigFieldItem[] = [];
            state.fieldItems?.forEach((item) => {
                const updatedItem = _.cloneDeep(item);
                if (item.ID == input.ID) {
                    updatedItem.Label = input.Label;
                    updatedItem.DefaultValue = input.DefaultValue;
                    updatedItem.FormatID = input.FormatID;
                }
                updatedFieldItems.push(updatedItem);
            });
            currentTemplate.FieldItems = updatedFieldItems;
            patchState(store, {currentTemplate: currentTemplate, fieldItems: updatedFieldItems});
        },
        setOriginalTemplate(input: TemplateConfigItem) {
            const state = getState(store);
            const template = _.cloneDeep(input);
            template.FieldItems = [];
            try {
                if (!state.isNew) {
                    getTemplateConfigFieldItems(template.ID, service).subscribe((items) => {
                        template.FieldItems = _.cloneDeep(items);
                        patchState(store, {templateId: template.ID, currentTemplate: template, originalTemplate: template, fieldItems: items});
                    });
                }
            } catch(error) {
                console.log(`Error when generating ID for new template: ${error}`);
            }
        },
        setIsNew(input: boolean): void {
            patchState(store, {isNew: input});
        },
        saveCurrentTemplate() {
            const state = getState(store);
            const item = _.cloneDeep(state.currentTemplate);
            item.FieldItems = state.fieldItems ?? [];
            return updateFlow(store, service, item);
        },
        revertToOriginalTemplate() {
            const state = getState(store);
            let result = false;
            if (state.isNew && state.templateId > 0) {
                remove(store, service).subscribe(() => {
                    result = true;
                });
            } else if (state.isNew) {
                result = true;
            } else {
                save(service, state.originalTemplate).subscribe(() => {
                    result = true;
                });
            }
            return result;
        },
        toggleLoading() {
            const state = getState(store);
            patchState(store, {loading: !state.loading});
        },
    })),
    /** LIFECYCLE HOOKS **/
    withHooks({
        onInit(store) {
            // on init, load the export template field format options so they're available to any component using the store
            getExportTemplateFieldFormatOptions(store);
        },
        onDestroy(store) {
            revertToInitialState(store);
        },
    }),
);